import {
  parseVisualGameAgentCallList,
  parseVisualGameAssetManifest,
  parseVisualGameCreateRunResponse,
  parseVisualGameDeleteSaveSlotResponse,
  parseVisualGameEndingText,
  parseVisualGamePack,
  parseVisualGamePackAssetUploadResponse,
  parseVisualGamePackCompileResponse,
  parseVisualGamePackPublishAcceptedResponse,
  parseVisualGameContentTranslationStatus,
  parseVisualGameContentTranslationResult,
  parseVisualGameContentTranslationImportResult,
  parseVisualGameContentTranslationInventory,
  parseVisualGameContentTranslationProgress,
  parseVisualGamePackDeleteResponse,
  parseVisualGameEditAuditLogList,
  parseVisualGamePackList,
  parseVisualGamePaywallInfo,
  parseVisualGamePreviewTokenResponse,
  parseVisualGameProgress,
  parseVisualGameProgressPatch,
  parseVisualGamePublishCheckList,
  parseVisualGameRunDetail,
  parseVisualGameStoryVideoLog,
  parseVisualGameRunList,
  parseVisualGameRunVideo,
  parseVisualGameRunVideoGeneration,
  parseVisualGameRunVideoGenerationResult,
  parseVisualGameIntentVideoGenerationResult,
  parseVisualGameMyIntentVideos,
  parseVisualGameRunIntentVideos,
  parseVisualGameNotification,
  parseVisualGameNotificationList,
  parseVisualGameScenarioVideoList,
  parseVisualGameShowcaseVideoPage,
  parseVisualGameVideoEngagement,
  parseVisualGameScenarioEngagement,
  parseVisualGameFavoriteScenarioList,
  parseVisualGameSeasonList,
  parseVisualGameRunEventList,
  parseVisualGameSaveSlot,
  parseVisualGameSaveSlotList,
  parseVisualGameScenarioVersion,
  parseVisualGameScenarioPage,
  parseVisualGameTtsVoiceList,
  parseVisualGameTalkingVideoPreloadList,
  parseVisualGameTurnEvent,
  parseVisualGameCreatorProject,
  parseVisualGameCreatorProjectList,
} from '@drama/contracts'

const ASSET_UPLOAD_TIMEOUT_MS = 60_000
const IMAGE_GENERATION_TIMEOUT_MS = 120_000
const CONTENT_TRANSLATION_TIMEOUT_MS = 600_000

/**
 * @typedef {object} VisualGameSpeechRequest
 * @property {string} text
 * @property {string} voice_id
 * @property {string} run_id
 * @property {'zh'|'zh-CN'|'en'|'ja'|'ko'|'es'|'pt'} locale
 * @property {string} idempotency_key
 * @property {string} [instruction]
 * @property {string} [emotion]
 * @property {number} [emotion_scale]
 * @property {number} [speech_rate]
 */

/** @param {unknown} value */
function parseScenarioList(value) {
  return parseVisualGameScenarioPage(value)
}

function parseRunVideoResponse(value) {
  if (!value || Object.getPrototypeOf(value) !== Object.prototype) {
    throw new Error('Visual game run video response must be an object')
  }
  return { video: value.video === null ? null : parseVisualGameRunVideo(value.video) }
}

function parseSpeechResponse(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Visual game speech response must be an object')
  }
  if (typeof value.audioUrl !== 'string' || value.audioUrl.length === 0) {
    throw new Error('Visual game speech audioUrl must be a non-empty string')
  }
  if (typeof value.request_id !== 'string' || value.request_id.length === 0) {
    throw new Error('Visual game speech request_id must be a non-empty string')
  }
  if (value.asset === null || typeof value.asset !== 'object' || Array.isArray(value.asset)) {
    throw new Error('Visual game speech asset must be an object')
  }
  if (typeof value.asset.id !== 'string' || value.asset.id.length === 0) {
    throw new Error('Visual game speech asset id must be a non-empty string')
  }
  return value
}

function parseTurnVideoResources(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value) || !Array.isArray(value.resources)) {
    throw new Error('Turn video resources response must contain resources')
  }
  return { resources: value.resources.map(turnVideoResource) }
}

function parseTurnVideoResourceReview(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('Turn video resource review response must be an object')
  return { resource: turnVideoResource(value.resource) }
}

function turnVideoResource(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('Turn video resource must be an object')
  return value
}

function parsePackRuns(value) {
  if (value === null || typeof value !== 'object' || !Array.isArray(value.runs)) {
    throw new Error('Pack runs response must contain runs')
  }
  return { runs: value.runs }
}

function experimentsPath(scenarioId, runId) {
  return `/api/visual-game/packs/${encodeURIComponent(scenarioId)}/runs/${encodeURIComponent(runId)}/experiments`
}

function parseExperiments(value) {
  if (value === null || typeof value !== 'object' || !Array.isArray(value.experiments)) {
    throw new Error('Turn video experiments response must contain experiments')
  }
  return value.experiments
}

function parseScreenplayScene(value) {
  if (value === null || typeof value !== 'object' || typeof value.scene?.prompt !== 'string') {
    throw new Error('Screenplay scene response must contain a scene prompt')
  }
  return value.scene
}

function parseRunStoryChain(value) {
  if (value === null || typeof value !== 'object' || !Array.isArray(value.turns) || !Array.isArray(value.resources)) {
    throw new Error('Run story chain response must contain turns and resources')
  }
  return value
}

function parseCreatorProjectDeleteResponse(value) {
  if (!value || Object.getPrototypeOf(value) !== Object.prototype || value.deleted !== true) {
    throw new Error('Creator project delete response must confirm deletion')
  }
  return value
}

function parseSeasonBranchDeleteResponse(value) {
  if (!value || Object.getPrototypeOf(value) !== Object.prototype) throw new Error('Season branch delete response must be an object')
  if (!Number.isInteger(value.deleted_seasons) || value.deleted_seasons < 1) throw new Error('Season branch deleted_seasons must be a positive integer')
  if (!Number.isInteger(value.deleted_runs) || value.deleted_runs < 1) throw new Error('Season branch deleted_runs must be a positive integer')
  return value
}

function parseAsrStreamMessage(value) {
  if (!value || Object.getPrototypeOf(value) !== Object.prototype) throw new Error('Visual game ASR stream message must be an object')
  if (!['ready', 'partial', 'transcript', 'final', 'error'].includes(value.type)) throw new Error(`Unhandled visual game ASR stream message: ${value.type}`)
  if ((value.type === 'partial' || value.type === 'transcript' || value.type === 'final') && typeof value.text !== 'string') throw new Error(`Visual game ASR ${value.type} text must be a string`)
  if (value.type === 'final') {
    if (typeof value.raw_text !== 'string') throw new Error('Visual game ASR final raw_text must be a string')
    if (value.text && value.mode !== 'act' && value.mode !== 'say') throw new Error(`Visual game ASR final mode is invalid: ${value.mode}`)
    if (!value.text && value.mode !== null) throw new Error('Visual game ASR empty final mode must be null')
  }
  if (value.type === 'error' && (typeof value.code !== 'string' || typeof value.message !== 'string')) throw new Error('Visual game ASR error message is invalid')
  return value
}

/** @param {{ request: Function, stream: Function }} transport */
export function createVisualGameApi(transport) {
  return {
    resolvePromotion(code) { return transport.request('POST', `/api/visual-game/promotions/${encodeURIComponent(code)}/resolve`) },
    guestTrialStatus() { return transport.request('GET', '/api/visual-game/guest-trial') },
    listHomepageCarousel() {
      return transport.request('GET', '/api/visual-game/homepage-carousel', {
        public: true,
        parse(value) {
          if (!value || !Array.isArray(value.items)) throw new Error('Homepage carousel response must contain items')
          return { items: value.items.map(item => {
            if (!item || typeof item.id !== 'string' || !['youtube', 'story'].includes(item.item_type) || typeof item.reference_id !== 'string') throw new Error('Homepage carousel item is invalid')
            return item
          }) }
        },
      })
    },
    listHomepagePopularPins() {
      return transport.request('GET', '/api/visual-game/homepage-popular-pins', {
        public: true,
        parse(value) {
          if (!value || !Array.isArray(value.items)) throw new Error('Homepage popular pins response must contain items')
          return { items: value.items.map(item => {
            if (!item || typeof item.id !== 'string' || typeof item.reference_id !== 'string') throw new Error('Homepage popular pin is invalid')
            return item
          }) }
        },
      })
    },
    listCreatorProjects() {
      return transport.request('GET', '/api/visual-game/creator/projects', { parse: parseVisualGameCreatorProjectList })
    },
    /** @param {{ idea_text: string, language: 'en'|'zh'|'ja'|'ko'|'es'|'pt', attachments?: Array<{file_name:string,mime:string,data_base64:string}> }} input */
    createCreatorProject(input) {
      return transport.request('POST', '/api/visual-game/creator/projects', { body: input, parse: parseVisualGameCreatorProject })
    },
    /** @param {string} id */
    getCreatorProject(id) {
      return transport.request('GET', `/api/visual-game/creator/projects/${encodeURIComponent(id)}`, { parse: parseVisualGameCreatorProject })
    },
    deleteCreatorProject(id) {
      return transport.request('DELETE', `/api/visual-game/creator/projects/${encodeURIComponent(id)}`, { parse: parseCreatorProjectDeleteResponse })
    },
    async listScenarios() {
      const items = []
      let page = 1
      while (true) {
        const result = await requestScenarioPage(transport, { page, page_size: 100 })
        items.push(...result.items)
        if (!result.has_more) return items
        page += 1
      }
    },
    /** @param {{ page?: number, page_size?: number, sort?: 'popular'|'latest' }} [input] */
    listScenarioPage(input = {}) {
      return requestScenarioPage(transport, {
        page: input.page ?? 1,
        page_size: input.page_size ?? 12,
        sort: input.sort ?? 'popular',
      })
    },
    /** @param {string} id */
    getAssetManifest(id) {
      return transport.request('GET', `/api/visual-game/assets/${encodeURIComponent(id)}`, {
        parse: parseVisualGameAssetManifest,
        public: true,
      })
    },
    /** @param {string} scenarioId @param {{ scenario_version_id?: string, version_id?: string, preview_token?: string }} [opts] */
    getPublicPack(scenarioId, opts = {}) {
      const params = new URLSearchParams()
      const versionId = opts.scenario_version_id || opts.version_id || ''
      if (versionId) params.set('scenario_version_id', versionId)
      if (opts.preview_token) params.set('preview_token', opts.preview_token)
      const query = params.toString()
      return transport.request('GET', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/pack${query ? `?${query}` : ''}`, {
        parse: parseVisualGamePack,
        public: !versionId || !!opts.preview_token,
      })
    },
    /**
     * @callchain.routeId visual-game.run.create
     * @param {{ scenario_id?: string, character_id?: string, season_id?: string, fork_from_season_id?: string, scenario_version_id?: string, preview_token?: string }} [body]
     */
    createRun(body) {
      return transport.request('POST', '/api/visual-game/runs', {
        routeId: 'visual-game.run.create',
        body,
        parse: parseVisualGameCreateRunResponse,
      })
    },
    /** @param {string} runId */
    getRun(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}`, {
        parse: parseVisualGameRunDetail,
      })
    },
    /** @param {string} runId */
    getTalkingVideoPreloads(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/talking-video-preloads`, {
        parse: parseVisualGameTalkingVideoPreloadList,
      })
    },
    /** @param {string} runId */
    getRunStoryVideoLog(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/story-video-log`, {
        parse: parseVisualGameStoryVideoLog,
      })
    },
    /** @param {string} runId */
    getGameplayReplay(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/gameplay-replay`, {
        parse: parseRunVideoResponse,
      })
    },
    /** @param {string} runId */
    requestGameplayReplay(runId) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(runId)}/gameplay-replay`, {
        body: {},
        parse: parseRunVideoResponse,
      })
    },
    /** @param {string} runId */
    downloadGameplayReplay(runId) {
      return transport.binary('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/gameplay-replay/download`)
    },
    /** @param {{ run_id: string, turn_id: string }} input */
    getGameplayReplayTurn({ run_id: runId, turn_id: turnId }) {
      return transport.request('GET', gameplayReplayTurnPath(runId, turnId), {
        parse: parseRunVideoResponse,
      })
    },
    /** @param {{ run_id: string, turn_id: string }} input */
    requestGameplayReplayTurn({ run_id: runId, turn_id: turnId }) {
      return transport.request('POST', gameplayReplayTurnPath(runId, turnId), {
        body: {},
        parse: parseRunVideoResponse,
      })
    },
    /** @param {{ run_id: string, turn_id: string }} input */
    downloadGameplayReplayTurn({ run_id: runId, turn_id: turnId }) {
      return transport.binary('GET', `${gameplayReplayTurnPath(runId, turnId)}/download`)
    },
    /** @param {{ run_id: string, turn_id: string, segment_order: number }} input */
    getGameplayReplaySegment({ run_id: runId, turn_id: turnId, segment_order: segmentOrder }) {
      return transport.request(
        'GET',
        gameplayReplaySegmentPath(runId, turnId, segmentOrder),
        { parse: parseRunVideoResponse },
      )
    },
    /** @param {{ run_id: string, turn_id: string, segment_order: number }} input */
    requestGameplayReplaySegment({ run_id: runId, turn_id: turnId, segment_order: segmentOrder }) {
      return transport.request(
        'POST',
        gameplayReplaySegmentPath(runId, turnId, segmentOrder),
        { body: {}, parse: parseRunVideoResponse },
      )
    },
    /** @param {{ run_id: string, turn_id: string, segment_order: number }} input */
    downloadGameplayReplaySegment({ run_id: runId, turn_id: turnId, segment_order: segmentOrder }) {
      return transport.binary('GET', `${gameplayReplaySegmentPath(runId, turnId, segmentOrder)}/download`)
    },
    /** @param {{ run_id: string, turn_no: number }} args */
    forkRunFromTurn({ run_id, turn_no }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/fork`, {
        body: { turn_no },
        parse: parseVisualGameRunDetail,
      })
    },
    /** @param {{ scenario_id: string, status?: string|null }} input */
    listTurnVideoResources({ scenario_id: scenarioId, status = null }) {
      const query = status === null ? '' : `?status=${encodeURIComponent(status)}`
      return transport.request('GET', `/api/visual-game/packs/${encodeURIComponent(scenarioId)}/turn-video-resources${query}`, {
        parse: parseTurnVideoResources,
      })
    },
    /** @param {{ scenario_id: string }} input */
    listPackRuns({ scenario_id: scenarioId }) {
      return transport.request('GET', `/api/visual-game/packs/${encodeURIComponent(scenarioId)}/runs`, {
        parse: parsePackRuns,
      })
    },
    /** @param {{ scenario_id: string, run_id: string }} input */
    getRunStoryChain({ scenario_id: scenarioId, run_id: runId }) {
      return transport.request(
        'GET',
        `/api/visual-game/packs/${encodeURIComponent(scenarioId)}/runs/${encodeURIComponent(runId)}/chain`,
        { parse: parseRunStoryChain },
      )
    },
    /** @param {{ scenario_id: string, run_id: string }} input */
    listRunExperiments({ scenario_id: scenarioId, run_id: runId }) {
      return transport.request('GET', experimentsPath(scenarioId, runId), { parse: parseExperiments })
    },
    /** @param {{ scenario_id: string, run_id: string, turn_id: string, prompt: string, duration: number, references: Array<{slot:number,url:string}>, previous_video_url?: string|null }} input */
    startRunExperiment({ scenario_id: scenarioId, run_id: runId, ...body }) {
      return transport.request('POST', experimentsPath(scenarioId, runId), {
        body,
        parse: value => value.experiment,
        timeoutMs: 30_000,
      })
    },
    /** @param {{ scenario_id: string, run_id: string, experiment_id: string }} input */
    adoptRunExperiment({ scenario_id: scenarioId, run_id: runId, experiment_id: experimentId }) {
      return transport.request('POST', `${experimentsPath(scenarioId, runId)}/${encodeURIComponent(experimentId)}/adopt`, {
        body: {},
        parse: value => value,
        timeoutMs: 60_000,
      })
    },
    /** @param {{ scenario_id: string, run_id: string, experiment_id: string }} input */
    discardRunExperiment({ scenario_id: scenarioId, run_id: runId, experiment_id: experimentId }) {
      return transport.request('POST', `${experimentsPath(scenarioId, runId)}/${encodeURIComponent(experimentId)}/discard`, {
        body: {},
        parse: value => value,
      })
    },
    /** @param {{ scenario_id: string, run_id: string, scene_no: number, prompt: string }} input */
    saveRunScreenplayScenePrompt({ scenario_id: scenarioId, run_id: runId, scene_no: sceneNo, prompt }) {
      return transport.request(
        'PUT',
        `/api/visual-game/packs/${encodeURIComponent(scenarioId)}/runs/${encodeURIComponent(runId)}/screenplay/scenes/${encodeURIComponent(sceneNo)}`,
        { body: { prompt }, parse: parseScreenplayScene },
      )
    },
    /** @param {{ scenario_id: string, resource_id: string, decision: 'publish'|'reject', rejection_reason?: string|null }} input */
    reviewTurnVideoResource({ scenario_id: scenarioId, resource_id: resourceId, decision, rejection_reason: rejectionReason = null }) {
      return transport.request('POST', `/api/visual-game/packs/${encodeURIComponent(scenarioId)}/turn-video-resources/${encodeURIComponent(resourceId)}/review`, {
        body: { decision, rejection_reason: rejectionReason },
        parse: parseTurnVideoResourceReview,
      })
    },
    /** @param {{ scenario_id?: string, series_id?: string }} [filters] */
    listRuns(filters = {}) {
      const params = new URLSearchParams()
      if (filters.scenario_id !== undefined) params.set('scenario_id', filters.scenario_id)
      if (filters.series_id !== undefined) params.set('series_id', filters.series_id)
      if (filters.run_ids !== undefined) params.set('run_ids', filters.run_ids.join(','))
      const query = params.toString()
      return transport.request('GET', `/api/visual-game/runs${query === '' ? '' : `?${query}`}`, {
        parse: parseVisualGameRunList,
      })
    },
    /** @param {string} seasonId */
    deleteSeasonBranch(seasonId) {
      return transport.request('DELETE', `/api/visual-game/seasons/${encodeURIComponent(seasonId)}`, {
        parse: parseSeasonBranchDeleteResponse,
      })
    },
    /** @param {string} runId */
    getRunVideoGeneration(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/video`, {
        parse: parseVisualGameRunVideoGeneration,
      })
    },
    /** @param {{ run_id:string, generation_request_id:string }} input */
    requestRunVideo({ run_id: runId, generation_request_id }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(runId)}/video`, {
        body: { variant: 'final', generation_request_id },
        parse: parseVisualGameRunVideoGenerationResult,
      })
    },
    /** 这一局能不能按需生成回合视频、报价、各回合生成状态。 @param {string} runId */
    getRunIntentVideos(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/intent-videos`, {
        parse: parseVisualGameRunIntentVideos,
      })
    },
    /** 当前玩家所有付费生成的回合视频，给视频生成管理页用。 */
    listMyIntentVideos() {
      return transport.request('GET', '/api/visual-game/me/intent-videos', {
        parse: parseVisualGameMyIntentVideos,
      })
    },
    /** @param {{ run_id: string, turn_id: string, generation_request_id: string }} input */
    requestIntentVideo({ run_id, turn_id, generation_request_id }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/intent-videos`, {
        body: { turn_id, generation_request_id },
        parse: parseVisualGameIntentVideoGenerationResult,
      })
    },
    /** @param {{ video_id: string, is_public: boolean }} args */
    updateOwnRunVideoVisibility({ video_id, is_public }) {
      return transport.request('PATCH', `/api/visual-game/me/videos/${encodeURIComponent(video_id)}`, {
        body: { is_public },
        parse: parseVisualGameRunVideo,
      })
    },
    /** @param {{ season_id: string, is_public: boolean }} args */
    updateOwnSeasonVideoVisibility({ season_id, is_public }) {
      return transport.request('PATCH', `/api/visual-game/me/seasons/${encodeURIComponent(season_id)}/videos`, {
        body: { is_public },
        parse: parseVisualGameScenarioVideoList,
      })
    },
    /** @param {string} videoId */
    deleteOwnRunVideo(videoId) {
      return transport.request('DELETE', `/api/visual-game/me/videos/${encodeURIComponent(videoId)}`, {
        parse: parseVisualGameDeleteSaveSlotResponse,
      })
    },
    /** @param {string} scenarioId */
    listScenarioVideos(scenarioId) {
      return transport.request('GET', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/videos`, {
        parse: parseVisualGameScenarioVideoList,
        public: true,
      })
    },
    /** @param {{ cursor?: string, sort?: 'latest'|'popular' }} [input] */
    listShowcaseVideos(input = {}) {
      const params = new URLSearchParams()
      if (input.cursor) params.set('cursor', input.cursor)
      if (input.sort) params.set('sort', input.sort)
      const query = params.toString()
      const suffix = query ? `?${query}` : ''
      return transport.request('GET', `/api/visual-game/videos${suffix}`, {
        parse: parseVisualGameShowcaseVideoPage,
        public: true,
      })
    },
    /** @param {string} playerId */
    listPlayerVideos(playerId) {
      return transport.request('GET', `/api/visual-game/players/${encodeURIComponent(playerId)}/videos`, {
        parse: parseVisualGameScenarioVideoList,
        public: true,
      })
    },
    listOwnVideos() {
      return transport.request('GET', '/api/visual-game/me/videos', {
        parse: parseVisualGameScenarioVideoList,
      })
    },
    /** @param {string} videoId */
    getVideo(videoId) {
      return transport.request('GET', `/api/visual-game/videos/${encodeURIComponent(videoId)}`, {
        parse: value => {
          const result = parseVisualGameScenarioVideoList(value)
          if (result.videos.length !== 1) throw new Error(`Video detail response must contain exactly one video: ${videoId}`)
          return result.videos[0]
        },
      })
    },
    /** @param {string} seasonId */
    listSeasonVideos(seasonId) {
      return transport.request('GET', `/api/visual-game/seasons/${encodeURIComponent(seasonId)}/videos`, {
        parse: parseVisualGameScenarioVideoList,
      })
    },
    listFavoriteVideos() {
      return transport.request('GET', '/api/visual-game/me/favorites', {
        parse: parseVisualGameScenarioVideoList,
      })
    },
    listFavoriteScenarios() {
      return transport.request('GET', '/api/visual-game/me/favorite-scenarios', {
        parse: parseVisualGameFavoriteScenarioList,
      })
    },
    /** @param {string} scenarioId */
    getScenarioEngagement(scenarioId) {
      return transport.request('GET', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/engagement`, {
        parse: parseVisualGameScenarioEngagement,
        public: true,
      })
    },
    /** @param {string} scenarioId */
    toggleScenarioLike(scenarioId) {
      return transport.request('POST', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/like`, {
        body: {},
        parse: parseVisualGameScenarioEngagement,
      })
    },
    /** @param {string} scenarioId */
    toggleScenarioFavorite(scenarioId) {
      return transport.request('POST', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/favorite`, {
        body: {},
        parse: parseVisualGameScenarioEngagement,
      })
    },
    listNotifications() {
      return transport.request('GET', '/api/visual-game/me/notifications', {
        parse: parseVisualGameNotificationList,
      })
    },
    /** @param {string} notificationId */
    markNotificationRead(notificationId) {
      return transport.request('PATCH', `/api/visual-game/me/notifications/${encodeURIComponent(notificationId)}/read`, {
        parse: parseVisualGameNotification,
      })
    },
    /** @param {string} videoId */
    getVideoEngagement(videoId) {
      return transport.request('GET', `/api/visual-game/videos/${encodeURIComponent(videoId)}/engagement`, {
        parse: parseVisualGameVideoEngagement,
      })
    },
    /** @param {string} videoId */
    recordVideoPlay(videoId) {
      return transport.request('POST', `/api/visual-game/videos/${encodeURIComponent(videoId)}/play`, {
        body: {},
        parse: parseVisualGameVideoEngagement,
        public: true,
      })
    },
    /** @param {string} videoId */
    toggleVideoLike(videoId) {
      return transport.request('POST', `/api/visual-game/videos/${encodeURIComponent(videoId)}/like`, {
        body: {},
        parse: parseVisualGameVideoEngagement,
      })
    },
    /** @param {string} videoId */
    toggleVideoFavorite(videoId) {
      return transport.request('POST', `/api/visual-game/videos/${encodeURIComponent(videoId)}/favorite`, {
        body: {},
        parse: parseVisualGameVideoEngagement,
      })
    },
    /** @param {string} scenarioId */
    listAvailableSeasons(scenarioId) {
      return transport.request('GET', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/seasons`, {
        parse: parseVisualGameSeasonList,
      })
    },
    /** @param {{ run_id: string, after_seq?: number, limit?: number }} args */
    listRunEvents({ run_id, after_seq, limit }) {
      const params = new URLSearchParams()
      if (after_seq != null) params.set('after_seq', String(after_seq))
      if (limit != null) params.set('limit', String(limit))
      const query = params.toString()
      return transport.request(
        'GET',
        `/api/visual-game/runs/${encodeURIComponent(run_id)}/events${query ? `?${query}` : ''}`,
        { parse: parseVisualGameRunEventList },
      )
    },
    /** @param {{ run_id: string, limit?: number }} args */
    listAgentCalls({ run_id, limit }) {
      const params = new URLSearchParams()
      if (limit != null) params.set('limit', String(limit))
      const query = params.toString()
      return transport.request(
        'GET',
        `/api/visual-game/runs/${encodeURIComponent(run_id)}/agent-calls${query ? `?${query}` : ''}`,
        { parse: parseVisualGameAgentCallList },
      )
    },
    /** @param {string} scenarioId */
    getProgress(scenarioId) {
      return transport.request('GET', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/progress`, {
        parse: parseVisualGameProgress,
      })
    },
    /** @param {string} scenarioId @param {import('@drama/contracts/visual-game').VisualGameProgressPatchT} patch */
    updateProgress(scenarioId, patch) {
      return transport.request('PATCH', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/progress`, {
        body: parseVisualGameProgressPatch(patch),
        parse: parseVisualGameProgress,
      })
    },
    /** @param {{ scenario_id: string, kind: 'episode'|'branch'|'ending'|'continue', key: string|number }} args */
    getPaywall({ scenario_id, kind, key }) {
      const params = new URLSearchParams()
      params.set('kind', kind)
      params.set('key', String(key))
      return transport.request(
        'GET',
        `/api/visual-game/scenarios/${encodeURIComponent(scenario_id)}/paywall?${params}`,
        { parse: parseVisualGamePaywallInfo },
      )
    },
    /** @param {string} scenarioId */
    listSaveSlots(scenarioId) {
      return transport.request('GET', `/api/visual-game/scenarios/${encodeURIComponent(scenarioId)}/save-slots`, {
        parse: parseVisualGameSaveSlotList,
      })
    },
    /** @param {{ run_id: string, slot_no: number, label?: string, overwrite?: boolean }} args */
    saveSlot({ run_id, slot_no, label, overwrite }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/save-slots`, {
        body: { slot_no, label, overwrite },
        parse: parseVisualGameSaveSlot,
      })
    },
    /** @param {{ scenario_id: string, slot_no: number }} args */
    restoreSaveSlot({ scenario_id, slot_no }) {
      return transport.request(
        'POST',
        `/api/visual-game/scenarios/${encodeURIComponent(scenario_id)}/save-slots/${encodeURIComponent(slot_no)}/restore`,
        { parse: parseVisualGameRunDetail },
      )
    },
    /** @param {{ scenario_id: string, slot_no: number }} args */
    deleteSaveSlot({ scenario_id, slot_no }) {
      return transport.request(
        'DELETE',
        `/api/visual-game/scenarios/${encodeURIComponent(scenario_id)}/save-slots/${encodeURIComponent(slot_no)}`,
        { parse: parseVisualGameDeleteSaveSlotResponse },
      )
    },
    /** @param {{ run_id: string, mode?: string, input: string, idempotency_key: string, turn_video_matching_enabled?: boolean, locale?: string }} args */
    submitTurn({ run_id, mode, input, idempotency_key, turn_video_matching_enabled, locale }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/turn`, {
        body: { mode, input, idempotency_key, turn_video_matching_enabled, locale },
        parse: parseVisualGameTurnEvent,
      })
    },
    /**
     * @param {{ turn_id: string, segment_order: number, segment_kind: 'action'|'speech',
     *   actor_type?: 'player'|'npc', event_type: 'started'|'completed'|'skipped'|'failed',
     *   attempt: number }} args
     */
    recordTurnPlaybackSegment({ turn_id, ...body }) {
      return transport.request(
        'POST',
        `/api/visual-game/turns/${encodeURIComponent(turn_id)}/playback-segments`,
        { body },
      )
    },
    /**
     * 上报前端这一侧的回合时间线；idempotency_key 是提交该回合时用的那一个。
     * @param {{ run_id: string, idempotency_key: string, marks: Array<{ name: string, at_ms: number }> }} args
     */
    recordTurnTimeline({ run_id, idempotency_key, marks }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/turn-timeline`, {
        body: { idempotency_key, marks },
      })
    },
    /** @param {{ run_id: string, mode?: string, input: string, idempotency_key: string, turn_video_matching_enabled?: boolean, locale?: string }} args */
    streamTurn({ run_id, mode, input, idempotency_key, turn_video_matching_enabled, locale }) {
      return transport.stream('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/turn/stream`, {
        body: { mode, input, idempotency_key, turn_video_matching_enabled, locale },
        parse: parseVisualGameTurnEvent,
      })
    },
    /** @param {{ run_id: string, location_id: string, idempotency_key: string, turn_video_matching_enabled?: boolean, locale?: string }} args */
    moveLocation({ run_id, location_id, idempotency_key, turn_video_matching_enabled, locale }) {
      return transport.request('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/move`, {
        body: { location_id, idempotency_key, turn_video_matching_enabled, locale },
        parse: parseVisualGameTurnEvent,
      })
    },
    /** @param {{ run_id: string, location_id: string, idempotency_key: string, turn_video_matching_enabled?: boolean, locale?: string }} args */
    streamMove({ run_id, location_id, idempotency_key, turn_video_matching_enabled, locale }) {
      return transport.stream('POST', `/api/visual-game/runs/${encodeURIComponent(run_id)}/move/stream`, {
        body: { location_id, idempotency_key, turn_video_matching_enabled, locale },
        parse: parseVisualGameTurnEvent,
      })
    },
    /** @param {string} runId */
    getEndingText(runId) {
      return transport.request('GET', `/api/visual-game/runs/${encodeURIComponent(runId)}/ending-text`, {
        parse: parseVisualGameEndingText,
      })
    },
    /** @param {VisualGameSpeechRequest} body */
    synthesizeSpeech(body) {
      return transport.request('POST', '/api/visual-game/tts', {
        body,
        parse: parseSpeechResponse,
      })
    },
    /**
     * 流式合成：音频分片边生成边到达，不等整段合成完成。返回值可用 `for await` 消费；
     * 每个事件是 `{type:'audio', seq, data(base64)}`，结束时是一条 `{type:'done', ...}`。
     * @param {VisualGameSpeechRequest} body
     */
    synthesizeSpeechStream(body) {
      return transport.stream('POST', '/api/visual-game/tts/stream', { body })
    },
    /** @param {{ turn_id: string, segment_order: number, actor_type: 'player'|'npc', audio_asset_id: string, tts_call_id: string }} body */
    recordTurnPlaybackAudio(body) {
      return transport.request('POST', `/api/visual-game/turns/${encodeURIComponent(body.turn_id)}/playback-audio`, {
        body: {
          segment_order: body.segment_order,
          actor_type: body.actor_type,
          audio_asset_id: body.audio_asset_id,
          tts_call_id: body.tts_call_id,
        },
      })
    },
    /** @param {{ run_id: string, locale: 'zh'|'zh-CN'|'en'|'ja'|'ko'|'es'|'pt' }} body @param {{ onMessage: Function, onClose: Function }} handlers */
    openAsrStream(body, handlers) {
      return transport.openWebSocket('/ws/asr', {
        start: { event: 'start', run_id: body.run_id, locale: body.locale },
        onMessage: message => handlers.onMessage(parseAsrStreamMessage(message)),
        onClose: handlers.onClose,
      })
    },
    listTtsVoices() {
      return transport.request('GET', '/api/admin/visual-game/tts-voices', {
        parse: parseVisualGameTtsVoiceList,
      })
    },
    /** @param {{ owner?: string, owner_id?: string, status?: string, visibility?: string, locale?: string }} [filter] */
    listPacks(filter = {}) {
      const params = new URLSearchParams()
      if (filter.owner) params.set('owner', filter.owner)
      if (filter.owner_id) params.set('owner_id', filter.owner_id)
      if (filter.status) params.set('status', filter.status)
      if (filter.visibility) params.set('visibility', filter.visibility)
      if (filter.locale) params.set('locale', filter.locale)
      const query = params.toString()
      return transport.request('GET', `/api/admin/visual-game/packs${query ? `?${query}` : ''}`, {
        parse: parseVisualGamePackList,
      })
    },
    /** @param {string} scenarioId */
    getPack(scenarioId) {
      return transport.request('GET', `/api/admin/visual-game/packs/${encodeURIComponent(scenarioId)}`, {
        parse: parseVisualGamePack,
      })
    },
    /** @param {{ scenario_id: string, version_id: string }} args */
    getPackVersion({ scenario_id, version_id }) {
      return transport.request(
        'GET',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}`,
        { parse: parseVisualGameScenarioVersion },
      )
    },
    /** @param {string} scenarioId */
    getPackLocalizationContext(scenarioId) {
      return transport.request('GET', `/api/admin/visual-game/packs/${encodeURIComponent(scenarioId)}/localization-context`, {
        parse: parseVisualGamePack,
      })
    },
    /** @param {{ title: string, locale?: string, visibility?: string, series_id?: string|null, series_title?: string|null, episode_no?: number|null }} body */
    createPack(body) {
      return transport.request('POST', '/api/admin/visual-game/packs', {
        body,
        parse: parseVisualGamePack,
      })
    },
    /** @param {{ id: string, title?: string, visibility?: string, series_id?: string|null, series_title?: string|null, episode_no?: number|null, tags?: string[] }} args */
    updatePack({ id, ...body }) {
      return transport.request('PATCH', `/api/admin/visual-game/packs/${encodeURIComponent(id)}`, {
        body,
        parse: parseVisualGamePack,
      })
    },
    /** @param {{ id: string }} args */
    duplicatePack({ id }) {
      return transport.request('POST', `/api/admin/visual-game/packs/${encodeURIComponent(id)}/duplicate`, {
        parse: parseVisualGamePack,
      })
    },
    /** @param {{ id: string }} args */
    deletePack({ id }) {
      return transport.request('DELETE', `/api/admin/visual-game/packs/${encodeURIComponent(id)}`, {
        parse: parseVisualGamePackDeleteResponse,
      })
    },
    /** @param {{ scenario_id: string, section_id: string, value: any }} args */
    savePackSection({ scenario_id, section_id, value }) {
      return transport.request(
        'PUT',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/sections/${encodeURIComponent(section_id)}`,
        {
          body: { value },
          parse: parseVisualGamePackCompileResponse,
        },
      )
    },
    /**
     * @param {{
     *   scenario_id: string,
     *   tag: string,
     *   type?: 'image'|'video'|'audio'|'asset',
     *   file_name?: string,
     *   mime?: string,
     *   data_base64?: string,
     *   data_url?: string,
     *   width?: number,
     *   height?: number,
     * }} args
     */
    uploadPackAsset({ scenario_id, ...body }) {
      return transport.request('POST', `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/assets`, {
        body,
        parse: parseVisualGamePackAssetUploadResponse,
        timeoutMs: ASSET_UPLOAD_TIMEOUT_MS,
      })
    },
    /** @param {{ scenario_id:string, tag:string, prompt:string, purpose?:'asset'|'portrait_cover', width?:number, height?:number, seed?:number }} args */
    generatePackImage({ scenario_id, ...body }) {
      return transport.request(
        'POST',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/assets/generate-image`,
        {
          body,
          parse: parseVisualGamePackAssetUploadResponse,
          timeoutMs: IMAGE_GENERATION_TIMEOUT_MS,
        },
      )
    },
    /** @param {{ scenario_id: string, tag: string }} args */
    deletePackAsset({ scenario_id, tag }) {
      return transport.request(
        'DELETE',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/assets/${encodeURIComponent(tag)}`,
        { parse: parseVisualGamePackAssetUploadResponse },
      )
    },
    /** @param {string} scenarioId */
    compilePack(scenarioId) {
      return transport.request('POST', `/api/admin/visual-game/packs/${encodeURIComponent(scenarioId)}/compile`, {
        parse: parseVisualGamePackCompileResponse,
      })
    },
    /** @param {{ scenario_id: string, version_id: string }} args */
    publishPackVersion({ scenario_id, version_id }) {
      return transport.request(
        'POST',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/publish`,
        { parse: parseVisualGamePackPublishAcceptedResponse },
      )
    },
    /** @param {{ scenario_id: string, version_id: string }} args */
    rollbackPackVersion({ scenario_id, version_id }) {
      return transport.request(
        'POST',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/rollback`,
        { parse: parseVisualGamePackCompileResponse },
      )
    },
    /** @param {{ scenario_id: string, version_id: string }} args */
    createPackPreviewToken({ scenario_id, version_id }) {
      return transport.request(
        'POST',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/preview-token`,
        { parse: parseVisualGamePreviewTokenResponse },
      )
    },
    /** @param {{ scenario_id: string, version_id: string }} args */
    listPackVersionChecks({ scenario_id, version_id }) {
      return transport.request(
        'GET',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/checks`,
        { parse: parseVisualGamePublishCheckList },
      )
    },
    /** @param {{ scenario_id: string, version_id: string }} args */
    getPackVersionTranslationStatus({ scenario_id, version_id }) {
      return transport.request(
        'GET',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/translations`,
        { parse: parseVisualGameContentTranslationStatus },
      )
    },
    /** @param {{ scenario_id: string, version_id: string, locale: 'en'|'ja'|'ko'|'es'|'pt', replace?: boolean }} args */
    translatePackVersion({ scenario_id, version_id, locale, replace = false }) {
      return transport.request(
        'POST',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/translations`,
        {
          body: { locale, replace },
          parse: parseVisualGameContentTranslationResult,
          timeoutMs: CONTENT_TRANSLATION_TIMEOUT_MS,
        },
      )
    },
    /** @param {{ scenario_id: string, version_id: string, locale: 'en'|'ja'|'ko'|'es'|'pt', items: Array<{ pointer: string, source_text: string, translated_text: string }>, metadata?: { title?: string, tagline?: string, series_title?: string } }} args */
    importPackVersionTranslations({ scenario_id, version_id, ...body }) {
      return transport.request(
        'POST',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/translations/import`,
        {
          body,
          parse: parseVisualGameContentTranslationImportResult,
        },
      )
    },
    /** @param {{ scenario_id: string, version_id: string, locale: 'en'|'ja'|'ko'|'es'|'pt' }} args */
    getPackVersionTranslationInventory({ scenario_id, version_id, locale }) {
      const params = new URLSearchParams({ locale })
      return transport.request(
        'GET',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/translations/inventory?${params.toString()}`,
        { parse: parseVisualGameContentTranslationInventory },
      )
    },
    /** @param {{ scenario_id: string, version_id: string, locale: 'en'|'ja'|'ko'|'es'|'pt' }} args */
    getPackVersionTranslationProgress({ scenario_id, version_id, locale }) {
      const params = new URLSearchParams({ locale })
      return transport.request(
        'GET',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/versions/${encodeURIComponent(version_id)}/translations/progress?${params.toString()}`,
        { parse: parseVisualGameContentTranslationProgress },
      )
    },
    /** @param {{ scenario_id: string, limit?: number }} args */
    listPackEditAuditLogs({ scenario_id, limit }) {
      const params = new URLSearchParams()
      if (limit != null) params.set('limit', String(limit))
      const query = params.toString()
      return transport.request(
        'GET',
        `/api/admin/visual-game/packs/${encodeURIComponent(scenario_id)}/audit-logs${query ? `?${query}` : ''}`,
        { parse: parseVisualGameEditAuditLogList },
      )
    },
  }
}

function requestScenarioPage(transport, { page, page_size, sort = 'popular' }) {
  const query = new URLSearchParams({ page: String(page), page_size: String(page_size), sort })
  return transport.request('GET', `/api/visual-game/scenarios?${query.toString()}`, { parse: parseScenarioList, public: true })
}

function gameplayReplaySegmentPath(runId, turnId, segmentOrder) {
  return `/api/visual-game/runs/${encodeURIComponent(runId)}/turns/${encodeURIComponent(turnId)}/segments/${segmentOrder}/gameplay-replay`
}

function gameplayReplayTurnPath(runId, turnId) {
  return `/api/visual-game/runs/${encodeURIComponent(runId)}/turns/${encodeURIComponent(turnId)}/gameplay-replay`
}
